import { Box, Typography, styled } from '@mui/material';
import { FC } from 'react';
import { Link, useMatch } from 'react-router-dom';
import { HEADER_HEIGHT, PAGE_WRAPPER_MARGIN_TOP } from '../../layout';

type NavGroup = {
  title: string;
  items: NavItem[];
};

type NavItem = {
  label: string;
  path: string;
  depth?: number;
};

const DocsSidebar: FC<{ navGroups: NavGroup[] }> = ({ navGroups }) => (
  <SidebarNav>
    {navGroups.map((group) => (
      <Box key={group.title}>
        <GroupHeader variant="caption">{group.title}</GroupHeader>
        {group.items.map((item) => (
          <SidebarItem key={item.path} item={item} />
        ))}
      </Box>
    ))}
  </SidebarNav>
);

const SidebarItem: FC<{ item: NavItem }> = ({ item }) => {
  const match = useMatch(`/docs/${item.path}`);
  const isActive = !!match;

  return (
    <NavLink to={`/docs/${item.path}`} active={isActive}>
      {item.label}
    </NavLink>
  );
};

const SIDEBAR_TOP = `${HEADER_HEIGHT + PAGE_WRAPPER_MARGIN_TOP}px`;

const SidebarNav = styled('nav')(({ theme }) => ({
  // Fixed width, doesnt collapse
  width: 240,
  flexShrink: 0,

  // Padding vertical
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),

  // Divider
  borderRight: `1px solid ${theme.palette.divider}`,

  // Spacing between groups
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),

  // Sticky position
  position: 'sticky',
  top: SIDEBAR_TOP,
  alignSelf: 'flex-start', // prevents flex stretch from making the Nav as tall as the parent, which would cause it to scroll upward when nearing the bottom of the page, also fixes divider being stretched to full height of page

  // Allow sidebar to scroll if content exceeds viewport height
  maxHeight: `calc(100vh - ${SIDEBAR_TOP})`,
  overflowY: 'auto'
}));

const GroupHeader = styled(Typography)(({ theme }) => ({
  display: 'block',
  padding: '6px 16px',
  marginBottom: theme.spacing(0.5),
  fontWeight: 700,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  fontSize: '0.7rem',
  backgroundColor: theme.palette.backgroundExtraLight.main
}));

const NavLink = styled(Link, { shouldForwardProp: (prop) => prop !== 'active' })<{ active: boolean }>(({ theme, active }) => ({
  display: 'block',
  padding: '6px 16px',
  textDecoration: 'none',
  fontSize: '0.875rem',
  color: active ? theme.palette.backgroundPrimary.main : theme.palette.text.primary,
  fontWeight: active ? 700 : 400,
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}));

export default DocsSidebar;
